import { useState, useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { AddCircleOutline } from '@mui/icons-material';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import validate from './form/validate';
import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';

import { ICarePlanIcd10Code } from '~/services/api/carePlanIcd10Codes/types';
import { IOption } from '~/ui/components/inputs/Select/types';
import { IIdName } from '~/services/api/types';
import { IFormFields } from './form/types';
import getInitialValues from '../../../Icd10Codes/form/getInitialValues';
import CarePlanIcd10Code from './CarePlanIcd10Code';

interface IProps {
  mode?: 'create' | 'edit';
  patientId: number;
  currentEditCarePlanIcd10Code?: ICarePlanIcd10Code;
  initialValues: ICarePlanIcd10Code;
  onClose: () => void;
  onCarePlanChange: () => void;
}

const ApplyCode = ({
  mode = 'create',
  patientId,
  currentEditCarePlanIcd10Code,
  initialValues,
  onClose,
  onCarePlanChange,
}: IProps) => {
  const [icd10CodeSelectors, setIcd10CodeSelectors] = useState<IIdName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const carePlanIcd10Codes = useStoreState(state => state.carePlanIcd10Codes.items);

  const { onCreateCarePlanIcd10Codes, onUpdateCarePlanIcd10Code } = useStoreActions(
    actions => actions.carePlanIcd10Codes,
  );

  const { showError } = useStoreActions(actions => actions.snackbar);

  const icd10CodeOptions: IOption[] = useMemo(
    () =>
      [
        ...(currentEditCarePlanIcd10Code
          ? [
              {
                value: currentEditCarePlanIcd10Code.icd10CodeId,
                label: `${currentEditCarePlanIcd10Code.icd10CodeName} - ${currentEditCarePlanIcd10Code.icd10CodeDescription}`,
              },
            ]
          : []),
        ...icd10CodeSelectors.map(item => ({
          value: item.id,
          label: item.name,
        })),
      ].filter(
        (option, index, options) =>
          options.findIndex(item => item.value === option.value) === index,
      ),
    [icd10CodeSelectors, currentEditCarePlanIcd10Code],
  );

  const getAvailableIcd10Codes = async () => {
    try {
      setIsLoading(true);
      const carePlanSelectors = await api.patients
        .getCarePlanSelectorsById(patientId)
        .then(res => res.data);
      setIcd10CodeSelectors(carePlanSelectors.icd10Codes);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCarePlanIcd10Codes = async (data: ICarePlanIcd10Code[]) => {
    try {
      setIsSaving(true);
      await onCreateCarePlanIcd10Codes({ patientId, data });
      onCarePlanChange();
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsSaving(false);
    }
  };

  const handleUpdateCarePlanIcd10Code = async (values: ICarePlanIcd10Code[]) => {
    try {
      setIsSaving(true);
      await onUpdateCarePlanIcd10Code(values[0]);
      onCarePlanChange();
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsSaving(false);
    }
  };

  const formMethods = useForm({
    resolver: validate,
    defaultValues: {
      carePlanIcd10Codes: [initialValues],
    },
  });

  const { control, handleSubmit } = formMethods;
  const watchedCarePlanIcd10Codes = useWatch({
    control,
    name: 'carePlanIcd10Codes',
  });

  const { fields, append, remove } = useFieldArray<IFormFields, 'carePlanIcd10Codes', ''>({
    control,
    name: 'carePlanIcd10Codes',
    keyName: '',
  });

  const assignedIcd10CodeIds = useMemo(
    () =>
      carePlanIcd10Codes
        .filter(item => item.id !== currentEditCarePlanIcd10Code?.id)
        .map(item => item.icd10CodeId),
    [carePlanIcd10Codes, currentEditCarePlanIcd10Code],
  );

  const getIcd10CodeOptions = (index: number) => {
    const currentSelectedIcd10CodeId = watchedCarePlanIcd10Codes?.[index]?.icd10CodeId;
    const selectedIcd10CodeIds = (watchedCarePlanIcd10Codes ?? [])
      .filter((item, itemIndex) => itemIndex !== index && item?.icd10CodeId)
      .map(item => item.icd10CodeId);

    const excludedIcd10CodeIds = new Set([...assignedIcd10CodeIds, ...selectedIcd10CodeIds]);

    return icd10CodeOptions.filter(
      option =>
        option.value === currentSelectedIcd10CodeId || !excludedIcd10CodeIds.has(option.value),
    );
  };

  const onAdd = () => append(getInitialValues({} as ICarePlanIcd10Code));

  const onRemove = (index: number) => remove(index);

  const getKey = (id: number, index: number) => `${id}-${index}`;

  const onSubmit = (values: ICarePlanIcd10Code[]) => {
    if (mode === 'create') {
      handleCreateCarePlanIcd10Codes(values);
    } else {
      handleUpdateCarePlanIcd10Code(values);
    }
  };

  const onMount = () => {
    getAvailableIcd10Codes();
  };

  useEffect(() => {
    onMount();
  }, []);

  if (isLoading) return <Loader />;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(values => onSubmit(values.carePlanIcd10Codes))}>
        <Grid container spacing={3}>
          {fields.map((carePlanIcd10Code, index) => (
            <Grid item xs={12} key={getKey(carePlanIcd10Code.id, index)}>
              <CarePlanIcd10Code
                index={index}
                isDeletable={fields.length > 1}
                icd10CodeOptions={getIcd10CodeOptions(index)}
                onRemove={onRemove}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {mode === 'create' && (
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<AddCircleOutline />}
                    onClick={onAdd}
                  >
                    Add More
                  </Button>
                </Grid>
              )}
              <Grid item xs />
              <Grid item>
                <Button variant="outlined" color="default" disabled={isSaving} onClick={onClose}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  {mode === 'create' ? 'Create' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default ApplyCode;
